import { useId, useLayoutEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Mount only while admission is suspended. The caller supplies safe retry UI;
 * no protected project content belongs inside this portal. Keeping the rest of
 * the DOM mounted preserves drafts without exposing body-level viewer portals.
 */
export function AccessSuspensionCurtain({ children }: { children: ReactNode }) {
    const id = `access-suspension-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
    const [host] = useState(() => document.createElement("div"));

    useLayoutEffect(() => {
        const previousFocus = document.activeElement;
        host.id = id;
        host.tabIndex = -1;
        host.setAttribute("role", "status");
        host.style.cssText =
            "position:fixed;inset:0;overflow:auto;display:grid;place-items:center;" +
            "background:var(--joy-palette-background-body);color:var(--joy-palette-text-primary);";
        const style = document.createElement("style");
        // A selector, rather than a snapshot of body children, covers portals
        // mounted after suspension too.
        // The detached host and stylesheet are installed before the next paint.
        style.textContent = `body > :not(#${id}) { display: none !important; }`;
        // A newly mounted Joy modal hides its body siblings for accessibility.
        // Only this owned safe host is exempt; protected portals stay hidden.
        const accessible = new MutationObserver(() => {
            host.removeAttribute("aria-hidden");
            host.removeAttribute("inert");
        });
        accessible.observe(host, { attributes: true, attributeFilter: ["aria-hidden", "inert"] });
        document.body.appendChild(host);
        document.head.appendChild(style);
        host.focus({ preventScroll: true });

        return () => {
            const restoreFocus = host.contains(document.activeElement);
            accessible.disconnect();
            style.remove();
            host.remove();
            if (restoreFocus && previousFocus instanceof HTMLElement && previousFocus.isConnected)
                previousFocus.focus({ preventScroll: true });
        };
    }, [host, id]);

    return createPortal(children, host);
}
